<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';

// Composables
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useInbox } from 'dashboard/composables/useInbox';

// Constants
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { REPLY_EDITOR_MODES } from 'components/widgets/WootWriter/constants';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
  ALLOWED_FILE_TYPES_FOR_LINE,
  ALLOWED_FILE_TYPES_FOR_INSTAGRAM,
} from 'shared/constants/messages';

// Components
import VideoCallButton from 'dashboard/components/widgets/VideoCallButton.vue';
import AIAssistanceButton from 'dashboard/components/widgets/AIAssistanceButton.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContextMenu from 'components/ui/ContextMenu.vue';
import SendMenuItems from 'next/widgets/WootWriter/SendMenuItems.vue';

// Props
const props = defineProps({
  mode: { type: String, default: REPLY_EDITOR_MODES.REPLY },
  sendButtonText: { type: String, default: '' },
  recordingAudioDurationText: { type: String, default: '00:00' },
  inbox: { type: Object, default: () => ({}) },
  showFileUpload: { type: Boolean, default: false },
  showAudioRecorder: { type: Boolean, default: false },
  isRecordingAudio: { type: Boolean, default: false },
  recordingAudioState: { type: String, default: '' },
  isSendDisabled: { type: Boolean, default: false },
  showScheduledMessagesButton: { type: Boolean, default: false },
  showEditorToggle: { type: Boolean, default: false },
  isOnPrivateNote: { type: Boolean, default: false },
  isInputDisabled: { type: Boolean, default: false },
  enableMultipleFileUpload: { type: Boolean, default: true },
  hasWhatsappTemplates: { type: Boolean, default: false },
  conversationId: { type: Number, required: true },
  message: { type: String, default: '' },
  newConversationModalActive: { type: Boolean, default: false },
  portalSlug: { type: String, required: true },
  conversationType: { type: String, default: '' },
});

// Emits
const emit = defineEmits([
  'send',
  'scheduleMessage',
  'fileUpload',
  'openScheduledMessages',
  'toggleEmojiPicker',
  'toggleAudioRecorder',
  'toggleAudioRecorderPlayPause',
  'replaceText',
  'toggleInsertArticle',
  'toggleEditor',
  'selectWhatsappTemplate',
]);

const store = useStore();
const { t } = useI18n();
const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
  useUISettings();

const {
  channelType,
  isALineChannel,
  isATwilioWhatsAppChannel,
  isAnInstagramChannel,
  isAWebWidgetInbox,
  isAPIInbox,
} = useInbox(props.inbox);

const uploadRef = ref(null);
const showContextMenu = ref(false);
const menuCoordinates = reactive({ x: 0, y: 0 });

const accountId = computed(() => store.getters.getCurrentAccountId);
const uiFlags = computed(() => store.getters['integrations/getUIFlags']);
const isFeatureEnabledonAccount = flag =>
  store.getters['accounts/isFeatureEnabledonAccount'](accountId.value, flag);

const isNote = computed(() => props.mode === REPLY_EDITOR_MODES.NOTE);

const wrapClass = computed(() => ({
  'is-note-mode': isNote.value,
}));

const showAttachButton = computed(() => props.showFileUpload || isNote.value);

const showAudioRecorderButton = computed(() => {
  if (isALineChannel.value) return false;
  return (
    isFeatureEnabledonAccount(FEATURE_FLAGS.VOICE_RECORDER) &&
    props.showAudioRecorder
  );
});

const showAudioPlayStopButton = computed(
  () => props.showAudioRecorder && props.isRecordingAudio
);

const isInstagramDM = computed(
  () => props.conversationType === 'instagram_direct_message'
);

const allowedFileTypes = computed(() => {
  if (isATwilioWhatsAppChannel.value)
    return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
  if (isALineChannel.value) return ALLOWED_FILE_TYPES_FOR_LINE;
  if (isAnInstagramChannel.value || isInstagramDM.value)
    return ALLOWED_FILE_TYPES_FOR_INSTAGRAM;
  return ALLOWED_FILE_TYPES;
});

const enableDragAndDrop = computed(() => !props.newConversationModalActive);

const audioRecorderPlayStopIcon = computed(() => {
  switch (props.recordingAudioState) {
    case 'playing':
      return 'i-ph-pause';
    case 'paused':
    case 'stopped':
      return 'i-ph-play';
    default:
      return 'i-ph-stop';
  }
});

const showMessageSignatureButton = computed(() => !props.isOnPrivateNote);

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(channelType.value)
);

const signatureToggleTooltip = computed(() =>
  sendWithSignature.value
    ? t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
    : t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP')
);

const enableInsertArticleInReply = computed(() => props.portalSlug);

const isFetchingAppIntegrations = computed(() => uiFlags.value.isFetching);

// Methods
const openContextMenu = e => {
  menuCoordinates.x = e.clientX;
  menuCoordinates.y = e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
};

const handleScheduleMessage = () => {
  closeContextMenu();
  emit('scheduleMessage');
};

const toggleMessageSignature = () => {
  setSignatureFlagForInbox(channelType.value, !sendWithSignature.value);
};

const replaceText = text => {
  emit('replaceText', text);
};

const toggleInsertArticle = () => {
  emit('toggleInsertArticle');
};

// Keyboard Events
const keyboardEvents = {
  '$mod+Alt+KeyA': {
    action: () => {
      // TODO: Hacky logic. Replace later.
      const uploadTriggerButton = document.querySelector(
        '#conversationAttachment'
      );
      if (uploadTriggerButton) uploadTriggerButton.click();
    },
    allowOnFocusedInput: true,
  },
};
useKeyboardEvents(keyboardEvents);

onMounted(() => {
  ActiveStorage.start();
});
</script>

<template>
  <div class="flex justify-between p-3" :class="wrapClass">
    <div class="left-wrap">
      <NextButton
        v-if="!isInputDisabled"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley-sticker"
        slate
        faded
        sm
        @click="$emit('toggleEmojiPicker')"
      />
      <FileUpload
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="file => emit('fileUpload', file)"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="$emit('toggleAudioRecorder')"
      />
      <NextButton
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        icon="i-ph-quotes"
        slate
        faded
        sm
        @click="$emit('toggleEditor')"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="$emit('toggleAudioRecorderPlayPause')"
      />
      <NextButton
        v-if="showMessageSignatureButton && !isInputDisabled"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="i-ph-signature"
        slate
        faded
        sm
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="hasWhatsappTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <VideoCallButton
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <AIAssistanceButton
        v-if="!isFetchingAppIntegrations"
        :conversation-id="conversationId"
        :is-private-note="isOnPrivateNote"
        :message="message"
        @replace-text="replaceText"
      />
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="fixed top-0 bottom-0 left-0 right-0 z-20 flex flex-col items-center justify-center w-full h-full gap-2 text-slate-900 dark:text-slate-50 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-slate-900 dark:text-slate-50">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <NextButton
        v-if="enableInsertArticleInReply"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
    </div>
    <div class="right-wrap">
      <NextButton
        v-if="showScheduledMessagesButton"
        icon="i-ph-clock"
        slate
        outline
        sm
        class="flex-shrink-0 mr-2"
        :title="$t('CONVERSATION.SCHEDULED_MESSAGES')"
        @click="$emit('openScheduledMessages')"
      />
      <NextButton
        :label="sendButtonText"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="$emit('send')"
        @contextmenu.prevent="openContextMenu"
      />
    </div>

    <ContextMenu
      v-if="showContextMenu"
      :x="menuCoordinates.x"
      :y="menuCoordinates.y"
      @close="closeContextMenu"
    >
      <SendMenuItems @schedule-message="handleScheduleMessage" />
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  @apply flex items-center;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply dark:bg-slate-800 bg-slate-100;
  }
}
</style>
